import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { AddIcon, DeleteIcon, SubtractIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'

import { useFormatPrice } from 'src/lib/js/hooks/useFormatPrice'
import { t } from 'src/translations'
import { useA11y } from 'public/components/online_ordering/useA11y'

type Props = {
  onQuantityChange: (
    quantity: number,
    change: number,
    defaultValueChange?: boolean
  ) => void
  defaultValue?: number
  min?: number
  max?: number
  allowDelete?: boolean
  onDelete?: () => void
  className?: string
  canIncrease?: boolean
  disabled?: boolean
  totalItemPrice?: number
  usesFractionalQuantity?: boolean
  isQuickAdd?: boolean
  disableAnnouncement?: boolean
}

type Quantity = {
  current: number
  previous: number
}

const QuantitySelector = ({
  min,
  max,
  defaultValue,
  className,
  onQuantityChange,
  allowDelete,
  onDelete,
  canIncrease,
  disabled,
  totalItemPrice = 0,
  usesFractionalQuantity,
  isQuickAdd,
  disableAnnouncement = false
}: Props) => {
  const [quantity, setQuantity] = useState<Quantity>({
    previous: 0,
    current: defaultValue || min || 0
  })
  const formattedPrice = useFormatPrice(totalItemPrice)
  const { announce } = useA11y()
  const decreaseButtonDisabled =
    disabled || (min != null ? quantity.current <= min : false)
  const increaseButtonDisabled =
    disabled || canIncrease === false || (max ? quantity.current >= max : false)

  useEffect(() => {
    const previous = defaultValue || min || 0
    const current = defaultValue || min || 0
    setQuantity({
      previous,
      current
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue])

  const getStatusMessage = useCallback(
    (qty: number) =>
      totalItemPrice > 0
        ? t('quantity-selector.total-price', {
            quantity: String(qty),
            formattedPrice
          })
        : t('quantity-selector.quantity-selected', { quantity: String(qty) }),
    [totalItemPrice, formattedPrice]
  )

  const statusMessage = useMemo(
    () => getStatusMessage(quantity.current),
    [getStatusMessage, quantity.current]
  )

  const onChange = useCallback(
    (quantity: Quantity) => {
      onQuantityChange(quantity.current, quantity.current - quantity.previous)
      setQuantity(quantity)
      if (!disableAnnouncement) {
        announce(getStatusMessage(quantity.current))
      }
    },
    [onQuantityChange, disableAnnouncement, announce, getStatusMessage]
  )

  const formatQuantity = (quantity: number) => {
    // Limits the quantity to 2 places after the decimal, and removes any trailing zeros
    return parseFloat(quantity.toFixed(2))
  }

  const incQuantity = useCallback(() => {
    onChange({
      current: formatQuantity(quantity.current + 1.0),
      previous: quantity.current
    })
  }, [onChange, quantity])
  const decQuantity = useCallback(() => {
    onChange({
      current: formatQuantity(quantity.current - 1.0),
      previous: quantity.current
    })
  }, [onChange, quantity])
  const enterQuantity = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        current: formatQuantity(parseFloat(e.target.value)),
        previous: quantity.current
      })
    },
    [onChange, quantity]
  )

  /**
   * These actions are defined by WAI-ARIA roles for spinbuttons.
   * @see https://www.w3.org/WAI/ARIA/apg/patterns/spinbutton/
   */
  const handleArrowKeys = useCallback(
    (e: any) => {
      switch (e.key) {
        case 'ArrowUp':
          e.stopPropagation()
          e.preventDefault()
          if (!canIncrease || (max && quantity.current === max)) {
            return
          }
          incQuantity()
          break
        case 'ArrowDown':
          e.stopPropagation()
          e.preventDefault()
          if (quantity.current === min) {
            return
          }
          decQuantity()
          break
        case 'Home':
          e.stopPropagation()
          e.preventDefault()
          if (min != null && quantity.current !== min) {
            onChange({
              current: min,
              previous: quantity.current
            })
          }
          break
        case 'End':
          e.stopPropagation()
          e.preventDefault()
          if (max && quantity.current !== max) {
            onChange({
              current: max,
              previous: quantity.current
            })
          }
          break
        default:
        // no-op
      }
    },
    [canIncrease, decQuantity, onChange, incQuantity, max, min, quantity]
  )

  return (
    <div
      className={classnames('quantitySelector', className, { disabled })}
      role='group'
    >
      {quantity.current > 1 ||
      !allowDelete ||
      (isQuickAdd && quantity.current > 0) ? (
        <button
          type='button'
          tabIndex={decreaseButtonDisabled ? -1 : 0}
          className='button subtract'
          aria-label={t('quantity-selector.decrease-quantity')}
          aria-disabled={decreaseButtonDisabled}
          disabled={decreaseButtonDisabled}
          onClick={(e) => {
            if (!decreaseButtonDisabled) {
              e.stopPropagation()
              decQuantity()
            }
          }}
        >
          <SubtractIcon accessibility='decorative' />
        </button>
      ) : (
        <button
          type='button'
          tabIndex={0}
          className='button delete'
          aria-label={t('quantity-selector.remove-modifier')}
          aria-disabled={disabled}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (onDelete) {
              onDelete()
            }
          }}
        >
          <DeleteIcon accessibility='decorative' />
        </button>
      )}
      {usesFractionalQuantity ? (
        <input
          type='number'
          inputMode='decimal'
          step='0.01'
          name='fractionalQuantityInput'
          aria-label={t('quantity-selector.quantity')}
          placeholder='1.00'
          defaultValue='1.00'
          disabled={disabled}
          min={min || 0}
          value={quantity.current}
          onChange={enterQuantity}
        />
      ) : (
        <span
          onKeyDown={handleArrowKeys}
          data-testid='spinner'
          role='spinbutton'
          className='quantity'
          tabIndex={0}
          aria-label={t('quantity-selector.quantity')}
          aria-valuetext={statusMessage}
          aria-valuenow={quantity.current}
          aria-valuemin={min || 0}
          aria-valuemax={max}
        >
          {quantity.current}
        </span>
      )}
      <button
        type='button'
        tabIndex={increaseButtonDisabled ? -1 : 0}
        className='button add'
        aria-label={t('quantity-selector.increase-quantity')}
        aria-disabled={increaseButtonDisabled}
        disabled={increaseButtonDisabled}
        onClick={(e) => {
          if (!increaseButtonDisabled) {
            e.stopPropagation()
            incQuantity()
          }
        }}
      >
        <AddIcon accessibility='decorative' />
      </button>
    </div>
  )
}

export default QuantitySelector
